import { readJsonScript } from "../../../utils/web";

import type { LegibilityFontPreference } from "./types";

type LoadedLegibilityFont = Exclude<LegibilityFontPreference, "default">;

export type LegibilityFontStylesheets = Record<LoadedLegibilityFont, string>;

/**
 * Unhashed fallback URL of each non-default font's stylesheet, served from
 * `indigo/static/fonts/`. Each file owns both the font source (`@font-face`
 * for self-hosted OpenDyslexic, `@import` of Google Fonts for the others) and
 * the `:root[data-legibility-font="..."]` rule that reassigns the design
 * system's `--font-family-*` custom properties. That reassignment is the
 * contract with NDS: it owns the properties, we own the override.
 *
 * In production these paths bypass Django's manifest hashing and nginx caches
 * `/static/` forever, so the server renders the hashed URLs into the page (see
 * `LEGIBILITY_FONT_STYLESHEETS_SCRIPT_ID`) and this map is only the fallback.
 *
 * A file is appended as a `<link>` only when the user has opted into that
 * font, so no alternate webfont is ever fetched for users on the default.
 */
export const LEGIBILITY_FONT_STYLESHEETS: LegibilityFontStylesheets = {
  "open-dyslexic": "/static/fonts/open-dyslexic.css",
  "atkinson-hyperlegible": "/static/fonts/atkinson-hyperlegible.css",
};

/**
 * `id` of the `json_script` block that indigo's `banking.html` renders with
 * the manifest-resolved stylesheet URL for each font.
 */
export const LEGIBILITY_FONT_STYLESHEETS_SCRIPT_ID =
  "legibility_font_stylesheets";

/**
 * Stylesheet URL per font: the server-rendered hashed URL when the page
 * provides one, otherwise the unhashed fallback.
 */
export const getLegibilityFontStylesheets = (): LegibilityFontStylesheets => {
  const resolved = { ...LEGIBILITY_FONT_STYLESHEETS };
  const fromPage = readJsonScript(LEGIBILITY_FONT_STYLESHEETS_SCRIPT_ID);
  if (!fromPage || typeof fromPage !== "object") return resolved;
  const urls = fromPage as Record<string, unknown>;
  (Object.keys(resolved) as LoadedLegibilityFont[]).forEach((font) => {
    const href = urls[font];
    if (typeof href === "string" && href) resolved[font] = href;
  });
  return resolved;
};

/** `id` attribute of the `<link>` that loads a given font's stylesheet. */
export const legibilityFontLinkId = (font: LoadedLegibilityFont): string =>
  `dbbl-legibility-font-${font}`;
